import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../components/storefront/Navbar';
import Footer from '../components/storefront/Footer';
import { useStore } from '../context/StoreContext';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase/supabase';
import { FiEdit2, FiChevronRight, FiMapPin, FiTag, FiX, FiCheck, FiChevronDown, FiSearch, FiArrowLeft, FiBookmark, FiEdit3 } from 'react-icons/fi';
import { SearchableCountrySelect } from '../components/ui/SearchableCountrySelect';
import SafeIcon from '../common/SafeIcon';
import LoadingSpinner from '../common/LoadingSpinner';

const ADDRESS_TITLES = ['Home', 'Office', 'Other'];

const Checkout = () => {
  const { cart, clearCart, user, profile, appSettings, shippingDistricts, checkUser } = useStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const [isMarxEnabled, setIsMarxEnabled] = useState(false);

  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);

  // District search state
  const [districtSearch, setDistrictSearch] = useState('');
  const [districtDropdownOpen, setDistrictDropdownOpen] = useState(false);
  const districtRef = useRef(null);

  // Save address state
  const [saveAddress, setSaveAddress] = useState(false);
  const [addressTitle, setAddressTitle] = useState('Home');
  const [customAddressTitle, setCustomAddressTitle] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    streetName: '',
    unitNumber: '',
    city: '',
    district: '',
    countryCode: '+94',
    phone: '',
    shippingMethod: 'delivery',
    paymentMethod: 'marx_ipg',
    orderNotes: ''
  });

  const [useSavedAddress, setUseSavedAddress] = useState(false);

  useEffect(() => {
    checkGatewayStatus();
    checkWelcomePromo();
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (districtRef.current && !districtRef.current.contains(e.target)) {
        setDistrictDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const checkGatewayStatus = async () => {
    const { data } = await supabase.from('payment_gateways').select('is_active').eq('name', 'marx_ipg').single();
    if (data?.is_active) setIsMarxEnabled(true);
    else setFormData(f => ({ ...f, paymentMethod: 'cod' }));
  };

  const checkWelcomePromo = async () => {
    if (!user) return;

    try {
      const enabledSetting = appSettings?.find(s => s.key === 'welcome_promo_enabled')?.value;
      if (enabledSetting !== 'true' && enabledSetting !== true) return;

      const { count } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .neq('status', 'draft');

      if (count && count > 0) return;

      const { data: promo } = await supabase
        .from('promo_codes')
        .select('*')
        .eq('code', 'WELCOME01')
        .eq('is_active', true)
        .maybeSingle();

      if (!promo) return;
      if (promo.expires_at && new Date(promo.expires_at) < new Date()) return;
      if (promo.max_uses && promo.uses_count >= promo.max_uses) return;

      setAppliedPromo(promo);
    } catch (err) {
      console.error('Error checking welcome promo:', err);
    }
  };

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        email: user.email || prev.email,
        firstName: prev.firstName || (profile?.full_name ? profile.full_name.split(' ')[0] : ''),
        lastName: prev.lastName || (profile?.full_name ? profile.full_name.split(' ').slice(1).join(' ') : '')
      }));

      if (profile?.saved_addresses && profile.saved_addresses.length > 0) {
        setUseSavedAddress(true);
        handleSelectSavedAddress(profile.saved_addresses[0]);
      }
    }
  }, [user, profile]);

  const handleSelectSavedAddress = (addr) => {
    setFormData(prev => ({
      ...prev,
      firstName: addr.firstName,
      lastName: addr.lastName,
      addressLine1: addr.addressLine1 || addr.address || '',
      addressLine2: addr.addressLine2 || '',
      streetName: addr.streetName || '',
      unitNumber: addr.unitNumber || '',
      city: addr.city,
      district: addr.district || prev.district,
      countryCode: addr.countryCode,
      phone: addr.phone
    }));
    if (addr.district) setDistrictSearch(addr.district);
  };

  const subtotal = cart.reduce((acc, item) => acc + (item.price * item.quantity), 0);

  const selectedDistrict = shippingDistricts.find(d => d.name === formData.district);
  const shipping = selectedDistrict ? Number(selectedDistrict.delivery_fee) : 0;
  const hasDistricts = shippingDistricts.length > 0;

  const filteredDistricts = shippingDistricts.filter(d =>
    d.name.toLowerCase().includes(districtSearch.toLowerCase())
  );

  const promoDiscount = appliedPromo
    ? appliedPromo.discount_type === 'percentage'
      ? Math.min((subtotal * appliedPromo.discount_value) / 100, subtotal)
      : Math.min(Number(appliedPromo.discount_value), subtotal)
    : 0;

  const total = Math.max(subtotal + shipping - promoDiscount, 0);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSelectDistrict = (district) => {
    setFormData(prev => ({ ...prev, district: district.name }));
    setDistrictSearch(district.name);
    setDistrictDropdownOpen(false);
  };

  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    setPromoError('');

    try {
      const { data, error: fetchError } = await supabase
        .from('promo_codes')
        .select('*')
        .eq('is_active', true)
        .ilike('code', promoInput.trim())
        .maybeSingle();

      if (fetchError || !data) {
        setPromoError('Invalid promo code. Please try again.');
        setPromoLoading(false);
        return;
      }

      if (data.expires_at && new Date(data.expires_at) < new Date()) {
        setPromoError('This promo code has expired.');
        setPromoLoading(false);
        return;
      }

      if (data.max_uses && data.uses_count >= data.max_uses) {
        setPromoError('This promo code has reached its usage limit.');
        setPromoLoading(false);
        return;
      }

      if (data.min_order && subtotal < Number(data.min_order)) {
        setPromoError(`Minimum order of Rs. ${Number(data.min_order).toFixed(2)} required.`);
        setPromoLoading(false);
        return;
      }

      setAppliedPromo(data);
      setPromoError('');
    } catch (err) {
      setPromoError('Error validating promo code.');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
    setPromoError('');
  };

  const handleNextStep = (e) => {
    e.preventDefault();

    if (step === 1 && hasDistricts && !formData.district) {
      setError('Please select a delivery district.');
      return;
    }

    setError('');
    if (step < 3) setStep(step + 1);
    else handleSubmitOrder();
  };

  const saveNewAddress = async () => {
    if (!user || !saveAddress) return;
    try {
      const currentAddresses = profile?.saved_addresses || [];
      const finalTitle = addressTitle === 'Other' ? (customAddressTitle.trim() || 'Other') : addressTitle;
      const newAddr = {
        id: Date.now().toString(),
        title: finalTitle,
        firstName: formData.firstName,
        lastName: formData.lastName,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        streetName: formData.streetName,
        unitNumber: formData.unitNumber,
        city: formData.city,
        district: formData.district,
        countryCode: formData.countryCode,
        phone: formData.phone
      };
      const updatedAddresses = [...currentAddresses, newAddr];
      await supabase
        .from('profiles')
        .update({ saved_addresses: updatedAddresses })
        .eq('id', user.id);
      await checkUser();
    } catch (err) {
      console.error('Failed to save address:', err);
    }
  };

  const handleSubmitOrder = async () => {
    if (cart.length === 0 || loading) return;
    
    setLoading(true);
    setError('');

    try {
      let orderId = pendingOrderId;
      const cartSnapshot = {
        items: cart,
        subtotal,
        shipping,
        discount: promoDiscount,
        total,
        currency: 'LKR',
        timestamp: new Date().toISOString()
      };

      const initialStatus = formData.paymentMethod === 'marx_ipg' ? 'draft' : 'pending';

      if (!orderId) {
        const { data: order, error: orderError } = await supabase
          .from('orders')
          .insert([{
            user_id: user?.id || null,
            total_amount: total,
            shipping_address: formData,
            status: initialStatus,
            payment_status: 'pending',
            order_snapshot: cartSnapshot,
            promo_code: appliedPromo?.code || null,
            promo_discount: promoDiscount || 0
          }])
          .select()
          .single();

        if (orderError) throw orderError;
        
        orderId = order.id;
        setPendingOrderId(orderId);

        const orderItems = cart.map(item => ({
          order_id: orderId,
          product_id: item.id,
          quantity: item.quantity,
          price: item.price
        }));

        const { error: itemsError } = await supabase.from('order_items').insert(orderItems);
        if (itemsError) throw itemsError;

        if (appliedPromo) {
          await supabase
            .from('promo_codes')
            .update({ uses_count: appliedPromo.uses_count + 1 })
            .eq('id', appliedPromo.id);
        }
      } else {
        await supabase.from('orders').update({
          shipping_address: formData,
          total_amount: total,
          status: initialStatus,
          order_snapshot: cartSnapshot,
          promo_code: appliedPromo?.code || null,
          promo_discount: promoDiscount || 0
        }).eq('id', orderId);
      }

      // Save address if user opted in
      await saveNewAddress();

      if (formData.paymentMethod === 'marx_ipg') {
        const { data: marxData, error: funcError } = await supabase.functions.invoke('marx-create-order', {
          body: {
            orderId: orderId,
            originUrl: window.location.origin,
            cartSnapshot
          }
        });

        if (funcError) throw new Error('Network error connecting to payment gateway.');
        if (marxData?.error) throw new Error(marxData.error);
        
        if (marxData?.paymentUrl) {
          window.location.href = marxData.paymentUrl;
          return;
        } else {
          throw new Error('Invalid gateway response format.');
        }
      } else {
        clearCart();

        await Promise.allSettled([
          supabase.functions.invoke('send-email', { body: { orderId: orderId } }),
          supabase.functions.invoke('send-email', { body: { orderId: orderId, admin_notification: true } })
        ]);

        alert('Order placed successfully!');
        navigate('/');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0 && !loading && !pendingOrderId) {
    navigate('/shop');
    return null;
  }

  const steps = ['Information', 'Payment', 'Review'];

  const addressDisplay = [
    formData.addressLine1,
    formData.addressLine2,
    formData.streetName,
    formData.unitNumber ? `Unit ${formData.unitNumber}` : null,
    formData.city,
    formData.district
  ].filter(Boolean).join(', ');

  const displayAddressTitle = addressTitle === 'Other' ? (customAddressTitle.trim() || 'Other') : addressTitle;

  return (
    <div className="bg-brand-sand min-h-screen pt-24 font-sans flex flex-col">
      <Navbar />
      
      <div className="flex-1 max-w-6xl mx-auto px-6 py-12 w-full grid grid-cols-1 lg:grid-cols-2 gap-12">
        
        <div className="bg-white/70 p-8 rounded-3xl border border-white shadow-sm flex flex-col">
          <div className="flex items-center text-xs font-bold uppercase tracking-widest text-brand-darkSage mb-8 overflow-x-auto pb-2">
            {steps.map((s, i) => (
              <React.Fragment key={s}>
                <span className={`whitespace-nowrap ${step >= i + 1 ? 'text-brand-gold' : 'opacity-50'}`}>
                  {i + 1}. {s}
                </span>
                {i < steps.length - 1 && <FiChevronRight className="mx-2 opacity-50" />}
              </React.Fragment>
            ))}
          </div>

          {error && <p className="text-red-500 mb-4 text-sm bg-red-50 p-3 rounded-lg border border-red-100">{error}</p>}

          <form onSubmit={handleNextStep} className="flex-1 flex flex-col">
            {step === 1 && (
              <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                <h2 className="text-3xl font-serif text-brand-darkSage mb-4">Contact & Shipping</h2>
                
                {!user && (
                  <div className="bg-white border border-brand-darkSage/20 p-4 rounded-xl mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm">
                    <div>
                      <p className="text-sm font-bold text-brand-darkSage mb-1">Have an account?</p>
                      <p className="text-xs text-brand-lightText">Sign in or sign up as a customer, or proceed as a guest below.</p>
                    </div>
                    <button 
                      type="button"
                      onClick={() => navigate('/login?redirect=/checkout')}
                      className="whitespace-nowrap bg-brand-darkSage text-white px-5 py-2.5 rounded-full text-xs uppercase tracking-widest font-bold hover:bg-brand-gold transition-colors"
                    >
                      Sign In / Sign Up
                    </button>
                  </div>
                )}

                {user && profile?.saved_addresses?.length > 0 && (
                  <div className="mb-6">
                    <div className="flex gap-4 mb-4">
                      <button 
                        type="button"
                        onClick={() => {
                          setUseSavedAddress(true);
                          handleSelectSavedAddress(profile.saved_addresses[0]);
                        }}
                        className={`text-xs font-bold uppercase tracking-widest pb-2 border-b-2 transition-colors ${useSavedAddress ? 'border-brand-gold text-brand-darkSage' : 'border-transparent text-gray-400 hover:text-gray-700'}`}
                      >
                        Saved Addresses
                      </button>
                      <button 
                        type="button"
                        onClick={() => {
                          setUseSavedAddress(false);
                          setFormData(f => ({ ...f, addressLine1: '', addressLine2: '', streetName: '', unitNumber: '', city: '', district: '', phone: '' }));
                          setDistrictSearch('');
                        }}
                        className={`text-xs font-bold uppercase tracking-widest pb-2 border-b-2 transition-colors ${!useSavedAddress ? 'border-brand-gold text-brand-darkSage' : 'border-transparent text-gray-400 hover:text-gray-700'}`}
                      >
                        New Address
                      </button>
                    </div>

                    {useSavedAddress && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {profile.saved_addresses.map(addr => (
                          <div 
                            key={addr.id}
                            onClick={() => handleSelectSavedAddress(addr)}
                            className={`p-4 rounded-xl border cursor-pointer transition-all ${
                              formData.addressLine1 === (addr.addressLine1 || addr.address) && formData.city === addr.city 
                                ? 'border-brand-gold bg-brand-gold/5 shadow-md' 
                                : 'border-brand-darkSage/20 bg-white hover:border-brand-gold/50'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-2 text-brand-darkSage">
                              <FiMapPin className="text-brand-gold" />
                              <span className="font-bold text-sm">{addr.firstName} {addr.lastName}</span>
                              {addr.title && (
                                <span className="text-[9px] font-black uppercase tracking-widest text-brand-gold bg-brand-gold/10 px-2 py-0.5 rounded-full">{addr.title}</span>
                              )}
                            </div>
                            <p className="text-xs text-brand-lightText line-clamp-1">{addr.addressLine1 || addr.address}</p>
                            <p className="text-xs text-brand-lightText">{addr.city}{addr.district ? `, ${addr.district}` : ''}</p>
                            <p className="text-xs text-brand-lightText mt-1 font-mono">{addr.countryCode} {addr.phone}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {(!useSavedAddress || !user) && (
                  <div className="space-y-4 animate-in fade-in duration-300">
                    <div>
                      <label className="block text-xs font-semibold text-brand-darkSage uppercase tracking-wider mb-1">Email *</label>
                      <input 
                        required type="email" name="email" value={formData.email} onChange={handleChange}
                        className="w-full border border-brand-darkSage/20 rounded-lg px-4 py-3 bg-white/50 focus:ring-brand-gold outline-none" 
                        placeholder="For order updates"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-brand-darkSage uppercase tracking-wider mb-1">First Name *</label>
                        <input 
                          required type="text" name="firstName" value={formData.firstName} onChange={handleChange}
                          className="w-full border border-brand-darkSage/20 rounded-lg px-4 py-3 bg-white/50 focus:ring-brand-gold outline-none" 
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-brand-darkSage uppercase tracking-wider mb-1">Last Name *</label>
                        <input 
                          required type="text" name="lastName" value={formData.lastName} onChange={handleChange}
                          className="w-full border border-brand-darkSage/20 rounded-lg px-4 py-3 bg-white/50 focus:ring-brand-gold outline-none" 
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-brand-darkSage uppercase tracking-wider mb-1">Address Line 1 *</label>
                      <input 
                        required type="text" name="addressLine1" value={formData.addressLine1} onChange={handleChange}
                        className="w-full border border-brand-darkSage/20 rounded-lg px-4 py-3 bg-white/50 focus:ring-brand-gold outline-none" 
                        placeholder="House/Building number and street"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-brand-darkSage uppercase tracking-wider mb-1">Address Line 2</label>
                      <input 
                        type="text" name="addressLine2" value={formData.addressLine2} onChange={handleChange}
                        className="w-full border border-brand-darkSage/20 rounded-lg px-4 py-3 bg-white/50 focus:ring-brand-gold outline-none" 
                        placeholder="Apartment, suite, floor, etc. (optional)"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-brand-darkSage uppercase tracking-wider mb-1">Street Name *</label>
                        <input 
                          required type="text" name="streetName" value={formData.streetName} onChange={handleChange}
                          className="w-full border border-brand-darkSage/20 rounded-lg px-4 py-3 bg-white/50 focus:ring-brand-gold outline-none" 
                          placeholder="Street / Road name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-brand-darkSage uppercase tracking-wider mb-1">Unit Number</label>
                        <input 
                          type="text" name="unitNumber" value={formData.unitNumber} onChange={handleChange}
                          className="w-full border border-brand-darkSage/20 rounded-lg px-4 py-3 bg-white/50 focus:ring-brand-gold outline-none" 
                          placeholder="Unit / Apt # (optional)"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-brand-darkSage uppercase tracking-wider mb-1">Town / City *</label>
                        <input 
                          required type="text" name="city" value={formData.city} onChange={handleChange}
                          className="w-full border border-brand-darkSage/20 rounded-lg px-4 py-3 bg-white/50 focus:ring-brand-gold outline-none" 
                        />
                      </div>
                      {hasDistricts && (
                        <div ref={districtRef} className="relative">
                          <label className="block text-xs font-semibold text-brand-darkSage uppercase tracking-wider mb-1">
                            District *
                          </label>
                          <div className="relative">
                            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-lightText/50 pointer-events-none" size={14} />
                            <input
                              required
                              type="text"
                              value={districtSearch}
                              onChange={(e) => {
                                setDistrictSearch(e.target.value);
                                setDistrictDropdownOpen(true);
                                if (!e.target.value) {
                                  setFormData(prev => ({ ...prev, district: '' }));
                                }
                              }}
                              onFocus={() => setDistrictDropdownOpen(true)}
                              className="w-full border border-brand-darkSage/20 rounded-lg pl-9 pr-10 py-3 bg-white/50 focus:ring-brand-gold outline-none"
                              placeholder="Search district..."
                            />
                            <FiChevronDown
                              className={`absolute right-3 top-1/2 -translate-y-1/2 text-brand-lightText/50 transition-transform cursor-pointer ${districtDropdownOpen ? 'rotate-180' : ''}`}
                              size={16}
                              onClick={() => setDistrictDropdownOpen(!districtDropdownOpen)}
                            />
                          </div>
                          {districtDropdownOpen && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-brand-darkSage/20 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                              {filteredDistricts.length === 0 ? (
                                <div className="px-4 py-3 text-xs text-gray-400 text-center">No districts found</div>
                              ) : (
                                filteredDistricts.map(d => (
                                  <button
                                    key={d.id}
                                    type="button"
                                    onClick={() => handleSelectDistrict(d)}
                                    className={`w-full text-left px-4 py-3 text-sm flex justify-between items-center hover:bg-brand-gold/5 transition-colors ${
                                      formData.district === d.name ? 'bg-brand-gold/10 text-brand-darkSage font-bold' : 'text-brand-darkSage'
                                    }`}
                                  >
                                    <span>{d.name}</span>
                                    <span className="text-xs text-brand-gold font-bold">Rs. {Number(d.delivery_fee).toFixed(2)}</span>
                                  </button>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-brand-darkSage uppercase tracking-wider mb-1">Phone Number *</label>
                      <div className="flex gap-2 relative">
                        <SearchableCountrySelect value={formData.countryCode} onChange={handleChange} />
                        <input 
                          required type="tel" name="phone" value={formData.phone} onChange={handleChange}
                          className="flex-1 border border-brand-darkSage/20 rounded-lg px-4 py-3 bg-white/50 focus:ring-brand-gold outline-none" 
                        />
                      </div>
                    </div>

                    {/* Save Address Option for logged-in users */}
                    {user && !useSavedAddress && (
                      <div className="bg-brand-gold/5 border border-brand-gold/20 rounded-xl p-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={saveAddress}
                            onChange={(e) => setSaveAddress(e.target.checked)}
                            className="w-4 h-4 accent-brand-gold rounded"
                          />
                          <div className="flex items-center gap-2">
                            <SafeIcon icon={FiBookmark} className="text-brand-gold" size={16} />
                            <span className="text-smfont-bold text-brand-darkSage">Save this address for future orders</span>
                          </div>
                        </label>
                        {saveAddress && (
                          <div className="mt-3 ml-7 space-y-3">
                            <div className="flex flex-wrap gap-2">
                              {ADDRESS_TITLES.map(title => (
                                <button
                                  key={title}
                                  type="button"
                                  onClick={() => { setAddressTitle(title); if (title !== 'Other') setCustomAddressTitle(''); }}
                                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest border transition-all ${
                                    addressTitle === title
                                      ? 'bg-brand-darkSage text-white border-brand-darkSage'
                                      : 'bg-white text-brand-darkSage/60 border-brand-darkSage/10 hover:border-brand-gold/50'
                                  }`}
                                >
                                  {title === 'Other' && <SafeIcon icon={FiEdit3} size={12} />}
                                  {title}
                                </button>
                              ))}
                            </div>
                            {addressTitle === 'Other' && (
                              <input
                                type="text"
                                value={customAddressTitle}
                                onChange={(e) => setCustomAddressTitle(e.target.value)}
                                placeholder="Enter custom title (e.g. Parents, Warehouse)"
                                className="w-full border border-brand-darkSage/20 rounded-lg px-4 py-2.5 bg-white/50 focus:ring-brand-gold outline-none text-sm"
                                maxLength={30}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {hasDistricts && formData.district && selectedDistrict && (
                      <div className="bg-brand-gold/5 border border-brand-gold/20 rounded-xl p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-brand-gold/20 rounded-lg flex items-center justify-center">
                            <FiMapPin className="text-brand-gold" size={16} />
                          </div>
                          <div>
                            <p className="text-xs font-bold text-brand-darkSage">Delivery to {formData.district}</p>
                            <p className="text-[10px] text-brand-lightText">Shipping fee applied</p>
                          </div>
                        </div>
                        <p className="text-sm font-bold text-brand-darkSage">Rs. {Number(selectedDistrict.delivery_fee).toFixed(2)}</p>
                      </div>
                    )}

                    {hasDistricts && !formData.district && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <p className="text-xs text-amber-700">
                          <strong>Please select a district</strong> to see the delivery fee. We currently deliver to: {shippingDistricts.map(d => d.name).join(', ')}.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                <h2 className="text-3xl font-serif text-brand-darkSage mb-6">Payment Method</h2>
                
                {isMarxEnabled && (
                  <label className={`flex items-center p-4 border rounded-xl cursor-pointer transition-colors ${formData.paymentMethod === 'marx_ipg' ? 'border-brand-gold bg-brand-gold/5' : 'border-brand-darkSage/20 hover:border-brand-gold/50'}`}>
                    <input 
                      type="radio" name="paymentMethod" value="marx_ipg" 
                      checked={formData.paymentMethod === 'marx_ipg'} onChange={handleChange}
                      className="accent-brand-gold w-4 h-4" 
                    />
                    <div className="ml-4 flex-1">
                      <span className="block font-semibold text-brand-darkSage">Pay Securely Online</span>
                      <span className="block text-xs text-brand-lightText">Visa, Mastercard, AMEX</span>
                    </div>
                  </label>
                )}

                <label className={`flex items-center p-4 border rounded-xl cursor-pointer transition-colors ${formData.paymentMethod === 'cod' ? 'border-brand-gold bg-brand-gold/5' : 'border-brand-darkSage/20 hover:border-brand-gold/50'}`}>
                  <input 
                    type="radio" name="paymentMethod" value="cod" 
                    checked={formData.paymentMethod === 'cod'} onChange={handleChange}
                    className="accent-brand-gold w-4 h-4" 
                  />
                  <span className="ml-4 font-semibold text-brand-darkSage">Cash on Delivery</span>
                </label>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                <button 
                  type="button" 
                  onClick={() => setStep(2)}
                  className="flex items-center gap-2 text-brand-darkSage font-bold text-xs uppercase tracking-widest hover:text-brand-gold transition-colors"
                >
                  <FiArrowLeft size={14} />
                  Back to Payment
                </button>

                <h2 className="text-3xl font-serif text-brand-darkSage">Review Order</h2>
                
                <div className="bg-brand-sand/50 p-6 rounded-2xl border border-brand-darkSage/10 space-y-6 text-sm text-brand-darkSage">
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-bold uppercase tracking-widest text-xs text-brand-gold">Contact & Shipping</h3>
                      <button type="button" onClick={() => setStep(1)} className="text-brand-lightText hover:text-brand-gold transition-colors"><FiEdit2 size={16} /></button>
                    </div>
                    <p>{formData.firstName} {formData.lastName}</p>
                    <p>{formData.email}</p>
                    <p>{addressDisplay}</p>
                    <p>{formData.countryCode} {formData.phone}</p>
                    {saveAddress && user && (
                      <p className="mt-2 text-xs text-brand-gold font-bold flex items-center gap-1">
                        <SafeIcon icon={FiBookmark} size={12} /> Saving as {displayAddressTitle}
                      </p>
                    )}
                  </div>

                  <div className="pt-4 border-t border-brand-darkSage/10">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-bold uppercase tracking-widest text-xs text-brand-gold">Shipping Method</h3>
                    </div>
                    <p>
                      {formData.district
                        ? `Delivery to ${formData.district} (Rs. ${shipping.toFixed(2)})`
                        : `Standard Delivery (Rs. ${shipping.toFixed(2)})`
                      }
                    </p>
                  </div>

                  <div className="pt-4 border-t border-brand-darkSage/10">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-bold uppercase tracking-widest text-xs text-brand-gold">Payment Method</h3>
                      <button type="button" onClick={() => setStep(2)} className="text-brand-lightText hover:text-brand-gold transition-colors"><FiEdit2 size={16} /></button>
                    </div>
                    <p>{formData.paymentMethod === 'cod' ? 'Cash on Delivery' : 'Secure Online Payment'}</p>
                  </div>
                </div>

                <button 
                  disabled={loading}
                  type="submit" 
                  className="w-full bg-brand-darkSage text-white py-4 px-10 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-brand-gold transition-all disabled:opacity-50 shadow-lg"
                >
                  {loading ? 'Processing...' : `Place Order (Rs. ${total.toFixed(2)})`}
                </button>
              </div>
            )}

            {step < 3 && (
              <div className="mt-8 pt-6 border-t border-brand-darkSage/10 flex justify-between items-center mt-auto">
                {step > 1 ? (
                  <button 
                    type="button" 
                    onClick={() => setStep(step - 1)}
                    className="text-brand-darkSage font-bold text-xs uppercase tracking-widest hover:text-brand-gold transition-colors"
                  >
                    &larr; Back
                  </button>
                ) : <div></div>}
                
                <button 
                  disabled={loading}
                  type="submit" 
                  className="bg-brand-darkSage text-white py-4 px-10 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-brand-gold transition-all disabled:opacity-50 shadow-lg"
                >
                  {loading ? 'Processing...' : 'Continue'}
                </button>
              </div>
            )}
          </form>
        </div>

        <div className="bg-brand-darkSage text-white p-8 rounded-3xl shadow-xl h-fit sticky top-24 lg:top-32">
          <h2 className="text-2xl font-serif mb-6 border-b border-white/10 pb-4">Order Summary</h2>
          
          <div className="space-y-4 mb-6 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
            {cart.map(item => (
              <div key={item.id} className="flex justify-between items-center text-sm">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/10 rounded-lg p-1"><img src={item.image} alt={item.name} className="w-full h-full object-contain" loading="lazy" /></div>
                  <div>
                    <p className="font-semibold line-clamp-1">{item.name}</p>
                    <p className="text-white/60 text-xs">Qty: {item.quantity}</p>
                  </div>
                </div>
                <p className="font-bold">Rs. {(item.price * item.quantity).toFixed(2)}</p>
              </div>
            ))}
          </div>

          <div className="mb-6 border-t border-white/10 pt-4">
            {appliedPromo ? (
              <div className="flex items-center justify-between bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiCheck} className="text-green-400" size={16} />
                  <div>
                    <p className="text-xs font-bold text-green-400">{appliedPromo.code}</p>
                    <p className="text-[10px] text-green-400/70">
                      {appliedPromo.discount_type === 'percentage'
                        ? `${appliedPromo.discount_value}% off`
                        : `Rs. ${Number(appliedPromo.discount_value).toFixed(2)} off`
                      }
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleRemovePromo}
                  className="text-white/40 hover:text-red-400 transition-colors p-1"
                >
                  <FiX size={16} />
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <FiTag className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={14} />
                    <input
                      type="text"
                      value={promoInput}
                      onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(''); }}
                      placeholder="Promo code"
                      className="w-full pl-9 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 outline-none focus:border-brand-gold/50"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleApplyPromo}
                    disabled={promoLoading || !promoInput.trim()}
                    className="px-4 py-2.5 bg-white/10 border border-white/10 rounded-lg text-xs font-bold uppercase tracking-widest hover:bg-white/20 transition-colors disabled:opacity-40"
                  >
                    {promoLoading ? '...' : 'Apply'}
                  </button>
                </div>
                {promoError && (
                  <p className="text-red-400 text-xs">{promoError}</p>
                )}
              </div>
            )}
          </div>

          <div className="space-y-3 text-sm border-t border-white/10 pt-6">
            <div className="flex justify-between text-white/80"><span>Subtotal</span><span>Rs. {subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-white/80">
              <span>Shipping{formData.district ? ` (${formData.district})` : ''}</span>
              <span>{shipping === 0 ? 'Free' : `Rs. ${shipping.toFixed(2)}`}</span>
            </div>
            {promoDiscount > 0 && (
              <div className="flex justify-between text-green-400">
                <span>Discount</span>
                <span>- Rs. {promoDiscount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-xl font-bold pt-4 border-t border-white/10 mt-2">
              <span>Total</span>
              <span className="text-brand-gold">Rs. {total.toFixed(2)}</span>
            </div>
          </div>
        </div>

      </div>
      <Footer />
    </div>
  );
};

export default Checkout;